import React, { useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { useMediaQuery } from 'react-responsive';
import Clamp from 'react-multiline-clamp';
import lodashCompact from 'lodash/fp/compact';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import {
  SR_API_ACTION_HOUSEHOLD_ADD_NOTE,
  SR_API_ACTION_HOUSEHOLD_PAY_SECURITY_DEPOSIT,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
} from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';

import { actionDataTableReset } from 'client/helpers/dataTables';

import {
  formatPrice,
  formatPhoneNumber,
} from 'common/formatters';

import {
  getMoveInAccessCode,
  getSecurityDepositSettings,
} from 'common/listingHelpers';

import {
  getHouseholdPets,
  calculateHouseholdDepositFees,
} from 'common/householdHelpers';

import {
  MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED,
  MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED,
  MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS,
} from 'common/marketingPartnerListingConstants';

import {
  JOB_ORDER_STATUS_CREATED,
  JOB_ORDER_STATUS_COMPLETED,
  JOB_ORDER_STATUS_FAILED,
  JOB_STATUS_FAILED,
  JOB_STATUS_COMPLETED,
  JOB_TYPE_YARD_SIGN_TAKEDOWN,
  JOB_TYPE_SUPRA_LOCK_TAKEDOWN,
  JOB_TYPE_SUNLOCK_TAKEDOWN,
} from 'common/jobConstants';

import {
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
} from 'common/listingConstants';

import {
  STRIPE_CHARGE_STATUS_PENDING,
  STRIPE_CHARGE_STATUS_SUCCESS,
  STRIPE_CHARGE_STATUS_FAILED,
  CHARGE_TYPE_OFFLINE,
} from 'common/stripeConstants';

import {
  PAYMENT_ACCOUNT_CARD,
  PAYMENT_ACCOUNT_BANK_ACCOUNT,
} from 'common/paymentAccountConstants';

import {
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
} from 'common/householdConstants';

import {
  getApplicantsList, sortHouseholdApplicationsByLeadRenter,
} from 'common/householdApplication/applicationHelpers';

import useBranch from 'client/hooks/useBranch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrIcon from 'client/components/SrIcon';
import SrStepsChecklist, { SR_STEPS_CHECKLIST_STATUS_FAILED } from 'client/components/SrStepsChecklist/SrStepsChecklist';
import SrHouseholdDownloadDocumentsButton from 'client/components/SrHouseholdDownloadDocumentsButton';
import SrPartnerConnectBankAccountButton from 'client/components/SrPartnerConnectBankAccountButton';
import SrOfflinePaymentReceivedModal from 'client/components/SrOfflinePaymentReceivedModal/SrOfflinePaymentReceivedModal';
import SrCardToggle from 'client/components/SrCardToggle/SrCardToggle';

import './SrHouseholdUpcomingMoveInCard.less';

const JOB_TYPE_TO_LABEL = {
  [JOB_TYPE_YARD_SIGN_TAKEDOWN]: 'Sign',
  [JOB_TYPE_SUPRA_LOCK_TAKEDOWN]: 'Supra',
  [JOB_TYPE_SUNLOCK_TAKEDOWN]: 'Sunlock',
};

const TakedownJob = ({
  job,
  listingId,
  jistixUrl,
  isCurrentUserAdmin,
}) => {
  const SrLinkOrSpan = isCurrentUserAdmin ? SrLink : 'span';
  let jobClassname = 'inProgress';
  let jobDateText = '';

  if (job.status === JOB_STATUS_FAILED) {
    jobClassname = 'failed';
  }
  else if (job.status === JOB_STATUS_COMPLETED) {
    jobClassname = 'completed';
  }

  if (job.completedDate) {
    jobDateText = [
      `${job.status === JOB_STATUS_FAILED ? 'Failed' : 'Completed'}`,
      `${moment(job.completedDate).format('MMM D')}`,
    ].join(': ');
  }
  else if (job.dueDate) {
    jobDateText = `Due: ${moment(job.dueDate).format('MMM D')}`;
  }

  const [text, setText] = useState(jobDateText);

  const onMouseEnter = () => {
    setText('View Details');
  };

  const onMouseLeave = () => {
    setText(jobDateText);
  };

  return (
    <SrFlex
      key={job.jobTypeId}
      styleName={classnames(
        'takedownJob',
        jobClassname
      )}
      justify='between'
    >
      <SrFlex align='center'>
        <div styleName='icon' />
        <span styleName='label'>
          {JOB_TYPE_TO_LABEL[job.jobTypeId]}
        </span>
      </SrFlex>
      <div>
        {jobDateText && (
          <SrLinkOrSpan
            styleName={classnames('date', isCurrentUserAdmin && 'link')}
            to={`${jistixUrl}listings/${listingId}/orders`}
            target='_blank'
            onMouseEnter={isCurrentUserAdmin ? onMouseEnter : null}
            onMouseLeave={isCurrentUserAdmin ? onMouseLeave : null}
          >
            {text}
          </SrLinkOrSpan>
        )}
      </div>
    </SrFlex>
  );
};

const SrHouseholdUpcomingMoveInCard = ({
  addressStateDisplay,
  addressStreetDisplay,
  moveInDate,
  renters,
  securityDepositLabel,
  guarantors,
  householdId,
  listingId,
  listingFeeSettings,
  partner,
  isCurrentUserAnonymous,
  householdApplications,
  leadRenterId,
  securityDepositCollectionInstructions,
  stripeAccount,
  isCurrentUserAdmin,
  securityDepositCharge,
  securityDepositChargeConfirmedBy,
  appAcceptedDate,
  leaseSignedDate,
  leaseSentDate,
  depositPaidDate,
  leaseDocument,
  leaseStartDate,
  householdListing,
  dispatch,
  currentUser,
  showPartnerName,
  sunlockLockCode,
  lockboxCode,
  propertyAccessCode,
  propertySetupAccessType,
  jobOrders,
  showUninstallOrderDetails,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const {
    jistixUrl,
  } = useBranch({
    jistixUrl: ['env', 'SIGN_OTTER_API_URL'],
  });

  const { nonEsaPets } = getHouseholdPets({ householdApplications });

  const listingSecurityDepositSettings = getSecurityDepositSettings({ listing: listingFeeSettings });

  const shouldCollectSecurityDepositAndFees = !!(
    listingSecurityDepositSettings.whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL // eslint-disable-line max-len
  );

  const depositFees = calculateHouseholdDepositFees({
    listing: listingFeeSettings,
    pets: nonEsaPets,
    householdListing,
  });

  const depositTotal = shouldCollectSecurityDepositAndFees
    ? formatPrice(depositFees.totalWithoutStripeFees)
    : formatPrice(listingFeeSettings.securityDeposit);

  const [offlinePaymentReceivedModalOpen, setOfflinePaymentReceivedModalOpen] = useState(false);

  const stepStatuses = {
    securityDeposit: MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED,
    draftLease: MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED,
    signLease: MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED,
    moveIn: MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED,
  };

  const onClickReceivedPayment = () => {
    const securityDepositApiPayload = {
      householdId,
      listingId,
      amount: depositFees.totalWithoutStripeFees,
      type: CHARGE_TYPE_OFFLINE,
    };

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_PAY_SECURITY_DEPOSIT,
      payload: {
        ...securityDepositApiPayload,
      },
      onSuccess: () => {
        setPaymentConfirmed(true);
        setTimeout(() => {
          setOfflinePaymentReceivedModalOpen(false);
          dispatch(actionDataTableReset, {
            dataSource: DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
          });
          dispatch(postWithState, {
            action: SR_API_ACTION_HOUSEHOLD_ADD_NOTE,
            payload: {
              householdId,
              note: `${securityDepositLabel} paid offline. Confirmed by ${currentUser.name}`,
            },
          });
        }, 1000);
      },
    });
  };

  const generateDepositPaymentText = () => {
    const depositComplete = !!securityDepositCharge;

    let depositPaymentText = depositComplete
      ? `Renter paid ${depositTotal}`
      : `Renter will pay ${depositTotal}`;

    if (!stripeAccount) {
      return depositPaymentText;
    }

    depositPaymentText += ' to';

    if (stripeAccount.displayName) {
      depositPaymentText += ` ${stripeAccount.displayName}`;
    }

    if (securityDepositCharge?.type === CHARGE_TYPE_OFFLINE) {
      depositPaymentText += ' offline.';
    }
    else if (
      stripeAccount?.type === PAYMENT_ACCOUNT_BANK_ACCOUNT
      && stripeAccount?.bankAccountLast4
    ) {
      depositPaymentText += ` at bank account ending in **${stripeAccount.bankAccountLast4}`;
    }
    else if (
      stripeAccount?.type === PAYMENT_ACCOUNT_CARD
      && stripeAccount?.cardLast4
    ) {
      depositPaymentText += ` debit card ending in **${stripeAccount.cardLast4}`;
    }

    return depositPaymentText;
  };

  const getUninstallOrderColor = () => {
    if (jobOrders.every((order) => order.status === JOB_ORDER_STATUS_COMPLETED)) {
      return 'green';
    }
    if (jobOrders.every((order) => order.status === JOB_ORDER_STATUS_CREATED)) {
      return 'yellowGold';
    }
    if (jobOrders.some((order) => order.status === JOB_ORDER_STATUS_FAILED)) {
      return 'red';
    }

    return 'yellowGold';
  };

  const getUninstallOrderIcon = () => {
    const color = getUninstallOrderColor();

    if (color === 'green') {
      return <img alt='Uninstall order successful' src='/images/checkmark-small-rounded-green.svg' />;
    }
    if (color === 'red') {
      return <img alt='Uninstall order failed' src='/images/red-x-icon-rounded.svg' />;
    }
    if (color === 'yellowGold') {
      return <img alt='Uninstall order in progress' src='/images/payment-transfer-status-yellow.svg' />;
    }

    return null;
  };

  const getFirstUninstallOrderByColor = () => {
    const color = getUninstallOrderColor();

    return jobOrders.find((jOrder) => {
      if (color === 'green') {
        return jOrder.status === JOB_ORDER_STATUS_COMPLETED;
      }
      if (color === 'red') {
        return jOrder.status === JOB_ORDER_STATUS_FAILED;
      }
      if (color === 'yellowGold') {
        return jOrder.status === JOB_ORDER_STATUS_CREATED;
      }

      return false;
    });
  };

  const getUninstallOrderText = () => {
    let text = 'Uninstall Order';
    const color = getUninstallOrderColor();

    if (color === 'green') {
      text = 'Completed:';
    }
    else if (color === 'red') {
      text = 'Failed:';
    }
    else if (color === 'yellowGold') {
      text = 'Created:';
    }

    const firstOrderDate = getFirstUninstallOrderByColor();

    if (!firstOrderDate) {
      return text;
    }

    const orderDate = color === 'green'
      ? jobOrders[jobOrders.length - 1]?.createdDate
      : firstOrderDate.createdDate;

    return `${text} ${momentWithAustinTimezone(orderDate).format('MMMM Do YYYY [at] h:mm A')}`;
  };

  if (householdApplications[0]?.status === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED) {
    stepStatuses.securityDeposit = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
  }
  else if (householdApplications[0]?.status === HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID) {
    stepStatuses.securityDeposit = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
  }
  else if (householdApplications[0]?.status === HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING) {
    stepStatuses.securityDeposit = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.draftLease = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
  }
  else if (householdApplications[0]?.status === HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT) {
    stepStatuses.securityDeposit = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.draftLease = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.signLease = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
  }
  else if (householdApplications[0]?.status === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED) {
    stepStatuses.securityDeposit = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.draftLease = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.signLease = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
    stepStatuses.moveIn = momentWithAustinTimezone().isSameOrAfter(leaseStartDate || moveInDate, 'day')
      ? MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED
      : MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
  }

  const viewDetailsLinkDOM = (
    <SrLink
      to={`/partner/${partner.slug}/listing/${householdListing.listingId}/approve/${householdListing.id}`}
    >
      <SrBox marginRight='4px' display='inlineBlock'>
        <SrIcon className='eye-icon-blue' />
      </SrBox>
      <span styleName='link small'>
        View Details
      </span>
    </SrLink>
  );

  const paymentReceivedOfflineDOM = partner.experienceConfiguration.offlinePaymentEnabled
    ? (
      <SrBox marginTop='6px' cursor='pointer' onClick={() => setOfflinePaymentReceivedModalOpen(true)}>
        <SrBox marginRight='4px' display='inlineBlock'>
          <SrIcon className='checkmark-rounded-blue-medium' />
        </SrBox>
        <span styleName='link small'>
          Payment Received Offline
        </span>
      </SrBox>
    )
    : null;

  const securityDepositCollectionInstructionsDOM = (
    <SrText
      font='greycliff'
      size='14'
      lineHeight='20px'
      color='blueGreycliff'
      weight='500'
    >
      Instructions for the renter:
      <Clamp
        lines={3}
        maxLines={1000}
        withToggle
        showMoreElement={({ toggle }) => (
          <SrText
            font='greycliff'
            size='14'
            lineHeight='20px'
            color='blue300'
            cursor='pointer'
            onClick={toggle}
            weight='500'
          >
            Show more
          </SrText>
        )}
        showLessElement={({ toggle }) => (
          <SrText
            font='greycliff'
            size='14'
            lineHeight='20px'
            color='blue300'
            cursor='pointer'
            onClick={toggle}
            weight='500'
          >
            Show less
          </SrText>
        )}
      >
        <span
          style={{
            fontFamily: 'Greycliff',
            margin: '8px 0',
          }}
        >
          {`"${securityDepositCollectionInstructions}"`}
        </span>
      </Clamp>
    </SrText>
  );

  const depositTotalDOM = (
    <>
      {!stripeAccount
        && (
          <SrBox marginBottom='8px'>
            <SrBox
              border='2px solid #EAEBEC'
              borderRadius='14px'
              boxSizing='border-box'
              padding='16px'
              marginTop='8px'
            >
              <SrText
                font='greycliff'
                align='center'
                weight='600'
                color='grey'
                size='14'
                lineHeight='17px'
                display='block'
              >
                {securityDepositCharge
                  && securityDepositCharge.status !== STRIPE_CHARGE_STATUS_FAILED
                  ? 'Renter Payment Initiated'
                  : 'Request for Payment Sent'
                }
              </SrText>
              <SrText
                font='greycliff'
                align='center'
                weight='800'
                color={securityDepositCharge ? 'green' : 'blueGreycliff'}
                fontSize='54px'
                lineHeight='56px'
                display='block'
              >
                {depositTotal}
              </SrText>
            </SrBox>
            <SrBox height={
              securityDepositCharge
              && securityDepositCharge.status !== STRIPE_CHARGE_STATUS_FAILED
                ? '88px'
                : '78px'}
            >
              {securityDepositCharge
                && securityDepositCharge.status !== STRIPE_CHARGE_STATUS_FAILED
                ? (
                  <SrBox marginTop='8px'>
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='500'
                      size='14'
                      lineHeight='20px'
                      align='center'
                      display='block'
                      opacity='0.6'
                    >
                      {`Initiated: ${
                        momentWithAustinTimezone(securityDepositCharge.createdDate)
                          .format('MMMM Do [at] h:mm A')
                      }`}
                    </SrText>
                  </SrBox>
                )
                : (
                  <SrBox marginTop='8px'>
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='500'
                      size='14'
                      lineHeight='20px'
                      align='center'
                      display='block'
                      opacity='0.6'
                    >
                      {`Requested: ${
                        momentWithAustinTimezone(appAcceptedDate)
                          .format('MMMM Do [at] h:mm A')
                      }`}
                    </SrText>
                  </SrBox>
                )
              }
              <SrFlex
                justify='center'
                marginTop={securityDepositCharge ? '8px' : '12px'}
              >
                {viewDetailsLinkDOM}
              </SrFlex>
            </SrBox>
            <SrPartnerConnectBankAccountButton
              partner={partner}
              listing={{
                id: listingId,
                addressStreetDisplay,
              }}
              householdListingId={householdListing.id}
            />
            <SrBox height='12px' />
            {!securityDepositCharge && (
              <SrBox marginTop='12px' marginBottom='22px' textAlign='center'>
                <span styleName='text light'>
                  We've sent a link for the renter to pay. Connect your bank account to receive the payment.
                </span>
              </SrBox>
            )}
          </SrBox>
        )
      }
      {stripeAccount
        && !securityDepositCharge
        && (
          <>
            <SrFlex justify='start' align='center'>
              <SrBox width='21px' height='20px' paddingRight='5px'>
                <img
                  alt='Payment pending'
                  src='/images/payment-transfer-status-yellow.svg'
                />
              </SrBox>
              <span styleName='paymentStatus inProgress'>
                {`Payment of ${depositTotal} Pending`}
              </span>
            </SrFlex>
            <SrBox marginTop='5px' marginBottom='6px'>
              <span styleName='text'>
                {
                  stripeAccount.type === PAYMENT_ACCOUNT_BANK_ACCOUNT
                    ? 'We\'ve sent a link for the renter to pay. When they do, it will be directly ' +
                    `deposited into your bank account ending in ${stripeAccount.bankAccountLast4}`
                    : 'We\'ve sent a link for the renter to pay. When they do, it will be directly ' +
                    `deposited into your debit card ending in ${stripeAccount.cardLast4}`
                }
              </span>
            </SrBox>
            <SrBox>
              {viewDetailsLinkDOM}
              {paymentReceivedOfflineDOM}
            </SrBox>
          </>
        )
      }
      {stripeAccount
        && securityDepositCharge
        && (
          <SrBox>
            {securityDepositCharge.status === STRIPE_CHARGE_STATUS_PENDING && (
              <>
                <SrFlex justify='start' align='center'>
                  <SrBox width='21px' height='20px' paddingRight='5px'>
                    <img
                      alt='Payment initiated'
                      src='/images/payment-transfer-status-yellow.svg'
                    />
                  </SrBox>
                  <span styleName='paymentStatus inProgress'>
                    {`Payment of ${depositTotal} Initiated`}
                  </span>
                </SrFlex>
                <SrBox marginTop='5px' marginBottom='6px'>
                  <span styleName='text'>
                    {`Initiated: ${
                      momentWithAustinTimezone(securityDepositCharge.createdDate)
                        .format('MMMM Do [at] h:mm A')
                    }`}
                    <br />
                    Estimated delivery: 3-7 biz days
                  </span>
                </SrBox>
                <SrBox>
                  {viewDetailsLinkDOM}
                </SrBox>
              </>
            )}
            {securityDepositCharge.status === STRIPE_CHARGE_STATUS_FAILED && (
              <>
                <SrBox marginBottom='10px'>
                  <span styleName='text'>
                    {generateDepositPaymentText()}
                  </span>
                </SrBox>
                <SrFlex justify='start' align='center'>
                  <SrBox width='15px' height='17px' paddingRight='5px'>
                    <img
                      alt='Transfer failed'
                      src='/images/red-x-icon-rounded.svg'
                    />
                  </SrBox>
                  <SrText
                    font='greycliff'
                    color='red'
                    weight='700'
                    size='16'
                    lineHeight='25px'
                  >
                    Transfer Failed
                  </SrText>
                </SrFlex>
                {securityDepositCharge.updatedDate && (
                  <SrBox marginTop='5px' marginBottom='6px'>
                    <SrText
                      font='greycliff'
                      color='red'
                      weight='500'
                      size='14'
                      lineHeight='20px'
                    >
                      {
                        `Failed ${
                          momentWithAustinTimezone(securityDepositCharge.updatedDate)
                            .format('MMMM Do [at] h:mm A')
                        }`
                      }
                    </SrText>
                  </SrBox>
                )}
                <SrBox marginTop='5px' marginBottom='6px'>
                  <SrText
                    font='greycliff'
                    color='red'
                    weight='600'
                    size='14'
                    lineHeight='20px'
                  >
                    Your account manager is looking into the reason
                    and we'll be in contact soon with a resolution.
                  </SrText>
                </SrBox>
                <SrBox>
                  {viewDetailsLinkDOM}
                </SrBox>
              </>
            )}
            {securityDepositCharge.status === STRIPE_CHARGE_STATUS_SUCCESS && (
              <>
                <SrBox marginBottom='10px'>
                  <span styleName='text'>
                    {generateDepositPaymentText()}
                  </span>
                  {securityDepositCharge.type === CHARGE_TYPE_OFFLINE
                    && securityDepositChargeConfirmedBy?.name
                    && (
                      <>
                        <SrText
                          display='block'
                          font='greycliff'
                          color='blueGreycliff'
                          size='14'
                          letterSpacing='0.5px'
                          weight='500'
                          lineHeight='20px'
                          opacity='0.6'
                        >
                          Confirmed by {securityDepositChargeConfirmedBy.name}
                        </SrText>
                        <SrText
                          display='block'
                          font='greycliff'
                          color='blueGreycliff'
                          size='14'
                          letterSpacing='0.5px'
                          weight='500'
                          lineHeight='20px'
                          opacity='0.6'
                        >
                          Completed {
                            moment(securityDepositCharge.createdDate).format('MMMM Do YYYY [at] h:mm a')
                          }
                        </SrText>
                      </>
                    )}
                </SrBox>
                {securityDepositCharge.stripeChargeId && (
                  <>
                    <SrFlex justify='start' align='center'>
                      <SrBox width='18px' height='18px' paddingRight='5px'>
                        <img
                          alt='Transfer successful'
                          src='/images/checkmark-small-rounded-green.svg'
                          width='16px'
                        />
                      </SrBox>
                      <span styleName='paymentStatus completed'>
                        Transfer Successful
                      </span>
                    </SrFlex>
                    <SrBox marginTop='5px' marginBottom='6px'>
                      <span styleName='text'>
                        {`Initiated: ${
                          momentWithAustinTimezone(securityDepositCharge.createdDate)
                            .format('MMMM Do [at] h:mm A')
                        }`}
                        <br />
                        Estimated delivery: 3-7 biz days
                      </span>
                    </SrBox>
                  </>
                )}
                <SrBox>
                  {viewDetailsLinkDOM}
                </SrBox>
              </>
            )}
          </SrBox>
        )
      }
    </>
  );

  const movedInBoxKeyUninstallStatus = (
    <>
      <SrBox marginBottom='8px'>
        {momentWithAustinTimezone().isSameOrBefore(leaseStartDate || moveInDate, 'day') && (
          <>
            <SrBox marginBottom='10px'>
              <span styleName='text'>
                Renter scheduled to move in {moment(leaseStartDate || moveInDate).format('MMM D')}. <br />
                {isCurrentUserAdmin ? 'Combo lockbox code:' : ''}
              </span>
            </SrBox>
            {isCurrentUserAdmin &&
              <SrBox
                marginBottom='10px'
                bgColor={lockboxCode ? 'grey200' : 'red200'}
                padding='8px 12px'
                borderRadius='14px'
                width='fit-content'
              >
                <SrText
                  font='greycliff'
                  color={lockboxCode ? 'black' : 'red'}
                  weight='700'
                  size='16'
                  lineHeight='22px'
                  align='center'
                >
                  {lockboxCode ? `Renter Code: ${lockboxCode}` : 'No Code Found'}
                </SrText>
              </SrBox>
            }
          </>
        )}
        {jobOrders.length > 0 && (
          <>
            <SrFlex justify='start' align='center' marginBottom='10px'>
              <SrBox width='18px' height='18px' paddingRight='5px'>
                {getUninstallOrderIcon()}
              </SrBox>
              <SrText
                color={getUninstallOrderColor()}
                styleName='jobOrderStatus'
              >
                Uninstall Order
              </SrText>
            </SrFlex>
            <SrBox marginBottom='10px'>
              <span styleName='text'>
                {getUninstallOrderText()}
              </span>
            </SrBox>
            {showUninstallOrderDetails && getFirstUninstallOrderByColor() && (
              <SrLink
                to={`${jistixUrl}listings/${listingId}/orders`}
                target='_blank'
              >
                <SrBox marginRight='4px' display='inlineBlock'>
                  <SrIcon className='eye-icon-blue' />
                </SrBox>
                <span styleName='link small'>
                  View Details
                </span>
              </SrLink>
            )}
          </>
        )}
      </SrBox>
    </>
  );

  const steps = [
    {
      label: 'Tenants Approved',
      sublabel: appAcceptedDate
        ? moment(appAcceptedDate).format('MMM D')
        : null,
      status: MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED,
      contentLeftPadding: true,
      content: viewDetailsLinkDOM,
    },
    {
      label: stepStatuses.securityDeposit === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED // eslint-disable-line max-len
        ? (listingFeeSettings.collectFirstMonth
          ? `Paid ${securityDepositLabel} & Rent`
          : `Paid ${securityDepositLabel}`
        )
        : (listingFeeSettings.collectFirstMonth
          ? `${securityDepositLabel} & Rent`
          : securityDepositLabel
        ),
      labelColor: stepStatuses.securityDeposit === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS // eslint-disable-line max-len
      && !(securityDepositCharge && securityDepositCharge.status === STRIPE_CHARGE_STATUS_FAILED)
        ? '#DBB000'
        : null,
      sublabel: depositPaidDate
        ? moment(depositPaidDate).format('MMM D')
        : securityDepositCharge && securityDepositCharge.status === STRIPE_CHARGE_STATUS_SUCCESS
          ? moment(securityDepositCharge.createdDate).format('MMM D')
          : null,
      status: securityDepositCharge
      && securityDepositCharge.status === STRIPE_CHARGE_STATUS_FAILED
        ? SR_STEPS_CHECKLIST_STATUS_FAILED
        : stepStatuses.securityDeposit,
      contentLeftPadding: shouldCollectSecurityDepositAndFees
        ? stripeAccount
        : securityDepositCollectionInstructions,
      content: shouldCollectSecurityDepositAndFees
        ? depositTotalDOM
        : securityDepositCollectionInstructionsDOM,
    },
    {
      label: stepStatuses.draftLease === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED // eslint-disable-line max-len
        ? 'Lease Drafted'
        : 'Draft Lease',
      status: stepStatuses.draftLease,
      sublabel: leaseSentDate
        ? moment(leaseSentDate).format('MMM D')
        : null,
    },
    {
      label: stepStatuses.signLease === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED // eslint-disable-line max-len
        ? 'Lease Signed'
        : 'Sign Lease',
      status: stepStatuses.signLease,
      sublabel: leaseSignedDate
        ? moment(leaseSignedDate).format('MMM D')
        : null,
      content: stepStatuses.signLease === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED // eslint-disable-line max-len
      && !isSmallScreen
      && leaseDocument
        ? (
          <SrBox marginLeft='30px' marginTop='-5px'>
            <SrLink download to={leaseDocument}>
              <span styleName='link medium'>
                <img
                  alt='download-icon'
                  src='/images/arrow-underlined-down-blue.svg'
                  width='9px'
                  height='12px'
                  style={{ marginRight: '6px' }}
                />
                Download Lease
              </span>
            </SrLink>
          </SrBox>
        )
        : null,
    },
  ];

  const yardSignTakedownJob = jobOrders.find((job) => job.jobTypeId === JOB_TYPE_YARD_SIGN_TAKEDOWN);
  const supraLockTakedownJob = jobOrders.find((job) => job.jobTypeId === JOB_TYPE_SUPRA_LOCK_TAKEDOWN);
  const sunlockTakedownJob = jobOrders.find((job) => job.jobTypeId === JOB_TYPE_SUNLOCK_TAKEDOWN);
  const takedownJobs = lodashCompact([
    yardSignTakedownJob,
    supraLockTakedownJob,
    sunlockTakedownJob,
  ]);

  let accessCodeText = '';
  let uninstallItemsStatus = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED;

  const moveInAccessCode = getMoveInAccessCode({
    lockboxCode,
    propertyAccessCode,
    propertySetupAccessType,
    sunlockLockCode,
  });

  if (takedownJobs.length > 0 && takedownJobs.every((job) => job.status === JOB_STATUS_COMPLETED)) {
    uninstallItemsStatus = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
  }
  else if (takedownJobs.length > 0) {
    uninstallItemsStatus = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
  }
  else if (stepStatuses.moveIn !== MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_NOT_STARTED) {
    uninstallItemsStatus = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED;
  }

  switch (moveInAccessCode.accessType) {
    case 'sunlock':
      accessCodeText = `Sunlock Code: ${sunlockLockCode}`;
      break;
    case 'lockbox':
      accessCodeText = `Access Code: ${propertyAccessCode}`;
      break;
    case 'comboLockbox':
      accessCodeText = `Lockbox Code: ${lockboxCode}`;
      break;
  }

  const uninstallItemsContent = (
    <div>
      {takedownJobs.map((job) => {
        return (
          <TakedownJob
            key={job.jobTypeId}
            job={job}
            listingId={listingId}
            jistixUrl={jistixUrl}
            isCurrentUserAdmin={isCurrentUserAdmin}
          />
        );
      })}
      {accessCodeText
      && uninstallItemsStatus === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS
      && (
        <div styleName='accessCodeBox'>
          <span>{accessCodeText}</span>
        </div>
      )}
    </div>
  );

  if (uninstallItemsStatus === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED) {
    // If uninstall marketing items are completed but the security deposit is not completed or initialized
    // not show as completed the uninstall section
    if (!securityDepositCharge || securityDepositCharge?.status === STRIPE_CHARGE_STATUS_FAILED) {
      uninstallItemsStatus = MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_IN_PROGRESS;
    }
  }

  steps.push({
    label: 'Uninstall Marketing Items',
    status: uninstallItemsStatus,
    contentLeftPadding: true,
    content: takedownJobs.length > 0
      ? uninstallItemsContent
      : null,
  });

  steps.push({
    label: stepStatuses.moveIn === MARKETING_PARTNER_LISTING_GOING_TO_MARKET_STEP_STATUS_COMPLETED // eslint-disable-line max-len
      ? 'Moved In'
      : 'Move In',
    status: stepStatuses.moveIn,
    sublabel: (leaseStartDate || moveInDate)
      ? moment(leaseStartDate || moveInDate).format('MMM D')
      : null,
    contentLeftPadding: true,
    content: movedInBoxKeyUninstallStatus,
  });

  const sortedHouseholdApplications = sortHouseholdApplicationsByLeadRenter({
    householdApplications: renters,
    leadRenterId,
  });

  const applicantsList = getApplicantsList({
    renters: sortedHouseholdApplications,
    guarantors,
  });

  return (
    <SrBox
      borderRadius={isSmallScreen ? '14px' : '24px'}
      boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
      backgroundColor='white'
      overflow='hidden'
    >
      <SrLink to={`/partner/${partner.slug}/listing/${listingId}`}>
        <SrBox
          padding='21px 20px 18px 20px'
          backgroundColor='#fff7d7'
          cursor='pointer'
        >
          <SrText
            color='green'
            weight='800'
            size='21'
            lineHeight='25px'
            font='greycliff'
          >
            {`Move-in ${momentWithAustinTimezone(leaseStartDate || moveInDate).format('MMMM Do')}`}
          </SrText>
          {showPartnerName && (
            <SrBox
              overflow='hidden'
              marginTop='6px'
              paddingBottom='0px'
              textOverflow='ellipsis'
            >
              <SrText
                font='greycliff'
                weight='400'
                fontSize='14px'
                lineHeight='14px'
                color='blue300'
              >
                {partner.displayName}
              </SrText>
            </SrBox>
          )}
          <SrBox
            overflow='hidden'
            marginTop='9px'
            paddingBottom='5px'
            textOverflow='ellipsis'
          >
            <SrText
              weight='800'
              size='16'
              lineHeight='25px'
              color='blueGreycliff'
              font='greycliff'
              whiteSpace='nowrap'
            >
              {addressStreetDisplay},
            </SrText>
            <SrText
              weight='500'
              size='16'
              lineHeight='25px'
              color='blueGreycliff'
              font='greycliff'
              whiteSpace='nowrap'
            >
              {' '} {addressStateDisplay}
            </SrText>
          </SrBox>
        </SrBox>
      </SrLink>
      {(renters.length >= 1 || guarantors.length >= 1) &&
      <SrCardToggle
        title={
          `${renters.length + guarantors.length} ` +
              `${pluralize('Tenant', renters.length + guarantors.length)}:`
        }
        label={applicantsList}
      >
        <SrBox padding='0 20px' marginBottom={isSmallScreen ? '23px' : null}>
          {sortedHouseholdApplications.map(({ id, name, phone, email }, index) => (
            <SrBox key={phone} marginTop={index > 0 ? '14px' : '-3px'}>
              <SrFlex justify='between' align='center'>
                <SrText
                  weight='600'
                  fontSize='16px'
                  font='greycliff'
                >
                  {name}
                </SrText>
                {isCurrentUserAdmin &&
                <SrLink to={`/admin/household/all/${id}`} target='_blank'>
                  <span styleName='link'>Edit</span>
                </SrLink>
                }
              </SrFlex>
              {(phone || email) &&
                <SrFlex marginTop='5px' direction='col'>
                  {phone && (
                    <SrText
                      font='greycliff'
                      lineHeight='17px'
                      fontSize='14px'
                      color='grey'
                      weight='600'
                    >
                      {formatPhoneNumber(phone)}
                    </SrText>
                  )}
                  {email && (
                    <SrText
                      font='greycliff'
                      fontSize='14px'
                      color='grey'
                      lineHeight='17px'
                      paddingTop='2px'
                      weight='600'
                    >
                      {email}
                    </SrText>
                  )}
                </SrFlex>
              }
            </SrBox>
          ))}
          {guarantors.map(({ name, phone, email }) => (
            <SrBox key={phone} marginTop={renters.length > 0 ? '14px' : '-3px'}>
              <SrFlex justify='between'>
                <span styleName='renterName'>{name}</span>
                <SrBox marginLeft='10px'>
                  <span styleName='isGuarantor'>Guarantor</span>
                </SrBox>

              </SrFlex>
              {(phone || email) &&
                <SrFlex marginTop='5px' direction='col'>
                  {phone && (
                    <SrText
                      font='greycliff'
                      lineHeight='17px'
                      fontSize='14px'
                      color='grey'
                      weight='600'
                    >
                      {formatPhoneNumber(phone)}
                    </SrText>
                  )}
                  {email && (
                    <SrText
                      font='greycliff'
                      fontSize='14px'
                      color='grey'
                      lineHeight='17px'
                      paddingTop='2px'
                      weight='600'
                    >
                      {email}
                    </SrText>
                  )}
                </SrFlex>
              }
            </SrBox>
          ))}
          {(!isCurrentUserAnonymous || partner.renterDocsPublic) &&
          <SrBox marginTop='21px' marginBottom='23px'>
            <SrHouseholdDownloadDocumentsButton
              listingId={listingId}
              householdId={householdId}
            />
          </SrBox>
          }
        </SrBox>
      </SrCardToggle>
      }
      <SrBox padding='15px 20px'>
        <SrText styleName='title'>
          Move-in Checklist:
        </SrText>
        <SrBox marginTop='16px' />
        <SrStepsChecklist
          checkMarkInsteadNumber
          steps={steps}
        />
      </SrBox>
      {offlinePaymentReceivedModalOpen && (
        <SrOfflinePaymentReceivedModal
          paymentConfirmed={paymentConfirmed}
          onClickReceivedPayment={onClickReceivedPayment}
          onClose={() => setOfflinePaymentReceivedModalOpen(false)}
        />
      )}
    </SrBox>
  );
};

SrHouseholdUpcomingMoveInCard.propTypes = {
  addressStateDisplay: PropTypes.string.isRequired,
  addressStreetDisplay: PropTypes.string.isRequired,
  moveInDate: PropTypes.string.isRequired,
  renters: PropTypes.array.isRequired,
  listingId: PropTypes.number.isRequired,
  listingFeeSettings: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  householdId: PropTypes.number.isRequired,
  isCurrentUserAnonymous: PropTypes.bool.isRequired,
  householdApplications: PropTypes.array.isRequired,
  leadRenterId: PropTypes.number.isRequired,
  securityDepositCollectionInstructions: PropTypes.string,
  stripeAccount: PropTypes.object,
  isCurrentUserAdmin: PropTypes.bool,
  securityDepositCharge: PropTypes.object,
  securityDepositChargeConfirmedBy: PropTypes.object,
  leaseSignedDate: PropTypes.string,
  appAcceptedDate: PropTypes.string,
  leaseSentDate: PropTypes.string,
  depositPaidDate: PropTypes.string,
  leaseDocument: PropTypes.string,
  leaseStartDate: PropTypes.string,
  householdListing: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  lockboxCode: PropTypes.number,
  jobOrders: PropTypes.array,
  showUninstallOrderDetails: PropTypes.bool,
  securityDepositLabel: PropTypes.string.isRequired,
};

SrHouseholdUpcomingMoveInCard.displayName = 'SrHouseholdUpcomingMoveInCard';

export default SrHouseholdUpcomingMoveInCard;
